// client/src/components/ApproveOrganiserList.js
import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

const fetchJSON = async (path, options = {}) => {
  const res = await apiFetch(path, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
};

export default function ApproveOrganiserList() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchJSON('/api/admin/approvals');
      setRows(Array.isArray(data?.organiserRequests) ? data.organiserRequests : []);
    } catch (e) {
      setError(e?.message || 'Failed to load organiser requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      setBusyId(id);
      await fetchJSON(`/api/admin/organiser-requests/${id}/approve`, { method: 'POST' });
      await load();
    } catch (e) {
      alert(`Approve failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Reason (optional):') || '';
    try {
      setBusyId(id);
      await fetchJSON(`/api/admin/organiser-requests/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      await load();
    } catch (e) {
      alert(`Reject failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <h2>Pending Organiser Requests</h2>
      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <p style={{ color: '#b91c1c' }}>Error: {error}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No pending requests.</p>
      ) : (
        <ul className="divide-y">
          {rows.map((r) => (
            <li key={r.id} className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium">{r.given_name} {r.surname}</div>
                <div className="text-xs text-gray-600">{r.email} • {r.mobile || '-'}</div>
                <div className="text-xs text-gray-500">Requested: {r.requested_at ? new Date(r.requested_at).toLocaleString() : '-'}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => reject(r.id)}
                  disabled={busyId === r.id}
                  aria-busy={busyId === r.id}
                  className="px-3 py-1 rounded-lg bg-red-600 text-white"
                >
                  {busyId === r.id ? 'Rejecting…' : 'Reject'}
                </button>
                <button
                  onClick={() => approve(r.id)}
                  disabled={busyId === r.id}
                  aria-busy={busyId === r.id}
                  className="px-3 py-1 rounded-lg bg-green-600 text-white"
                >
                  {busyId === r.id ? 'Approving…' : 'Approve'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}